import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import Offers, {
  OfferCard,
  OfferDetails,
} from 'app/apiService/offers/offersApiService';

// API Response types based on the image
interface ApiMeta {
  totalDocuments: number;
  totalPages: number;
  currentPage: number;
}

interface ApiResponse {
  data: OfferCard[];
  meta: ApiMeta;
}

// ----------- Type Definitions -----------
interface OffersContextType {
  loading: boolean;
  error: string;
  offers: OfferCard[];
  selectedOffer: OfferDetails | null;

  // Dealer selection
  selectedDealerId: string;
  selectedDealerName: string;

  // Infinite scroll properties
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  totalOffers: number;
  currentPage: number;

  addOffer: (offer: Partial<OfferDetails> & { title: string }) => Promise<void>;
  deleteOffer: (offerId: string) => Promise<void>;
  editOffer: (offerId: string, data: Partial<OfferDetails>) => Promise<void>;
  getOffer: (offerId: string) => Promise<void>;
  refetchOffers: () => void;
  resetAndRefetchOffers: () => void;
  enableQuery: () => void;

  // Filters for infinite query
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  categoryFilter: string;
  setCategoryFilter: (category: string) => void;
  drivetrainFilter: string;
  setDrivetrainFilter: (drivetrain: string) => void;
  isActiveFilter: boolean | null;
  setIsActiveFilter: (isActive: boolean | null) => void;
  priceRangeFilter: [number, number];
  setPriceRangeFilter: (priceRange: [number, number]) => void;
  vehicleYearFilter: number | null;
  setVehicleYearFilter: (year: number | null) => void;
  discountTypeFilter: string;
  setDiscountTypeFilter: (type: string) => void;
}

export const OffersContext = createContext<OffersContextType | undefined>(
  undefined,
);

// Custom hook for debouncing
const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const OffersProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState<string>('');
  const [selectedOffer, setSelectedOffer] = useState<OfferDetails | null>(null);
  const [isQueryEnabled, setIsQueryEnabled] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer?.dealer || '';

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [drivetrainFilter, setDrivetrainFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | null>(true);
  const [priceRangeFilter, setPriceRangeFilter] = useState<[number, number]>([
    0, 100000,
  ]);
  // Additional schema-based filters
  const [vehicleYearFilter, setVehicleYearFilter] = useState<number | null>(
    null,
  );
  const [discountTypeFilter, setDiscountTypeFilter] = useState('');

  // Debounced values for API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay for search
  const debouncedCategoryFilter = useDebounce(categoryFilter, 300); // 300ms delay for dropdowns
  const debouncedDrivetrainFilter = useDebounce(drivetrainFilter, 300);
  const debouncedIsActiveFilter = useDebounce(isActiveFilter, 300);
  const debouncedPriceRangeFilter = useDebounce(priceRangeFilter, 800); // 800ms delay for range slider
  const debouncedVehicleYearFilter = useDebounce(vehicleYearFilter, 300);
  const debouncedDiscountTypeFilter = useDebounce(discountTypeFilter, 300);
  // Add debounced values for ALL filters to prevent unnecessary API calls
  const debouncedStatusFilter = useDebounce(statusFilter, 300);
  const debouncedSortBy = useDebounce(sortBy, 300);

  const ITEMS_PER_PAGE = 9; // Updated back to 9 for skip=0 limit=9

  // Mock data for fallback
  const mockOffers: OfferCard[] = [
    {
      _id: '1',
      title: 'BMW X5 Special Offer',
      headline: 'Experience Luxury and Performance Like Never Before',
      content:
        'Drive home in the all-new BMW X5 with premium features, advanced technology, and unmatched performance. This exclusive offer includes comprehensive warranty and maintenance packages.',
      name: 'BMW X5 Deal',
      description: 'Premium luxury SUV with advanced features',
      offer_type: 'LEASE',
      offer_image_url:
        'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/bmw-x5-offer',
      vehicle_make: 'BMW',
      vehicle_model: 'X5',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop',
      msrp: 75000,
      selling_price: 63750,
      drivetrain: 'AWD',
      trim: 'xDrive40i',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      stock_number: 'BMW001',
      model_code: 'G05',
      dealership_id: 'dealer_1',
      vin: '5UXCR6C0XPL123456',
      disclaimer: 'Valid for qualified buyers only. See dealer for details.',
      eligible_regions: ['ALL'],
      conditions: ['Must finance through BMW Financial'],
      lease_terms: [
        {
          monthly_payment: 899,
          due_at_signing: 5000,
          lease_term_months: 36,
          annual_mileage_limit: 12000,
        },
      ],
    },
    {
      _id: '2',
      title: 'Tesla Model 3 Deal',
      headline: 'Electric Innovation at Its Finest',
      content:
        'Join the electric revolution with the Tesla Model 3. Featuring autopilot capabilities, supercharging network access, and zero emissions driving.',
      name: 'Tesla Model 3',
      description: 'Electric sedan with autopilot technology',
      offer_type: 'FINANCE',
      offer_image_url:
        'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/tesla-model3-offer',
      vehicle_make: 'TESLA',
      vehicle_model: 'MODEL 3',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=250&fit=crop',
      msrp: 45000,
      selling_price: 36000,
      drivetrain: 'AWD',
      trim: 'Long Range',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-25',
      stock_number: 'TSL002',
      model_code: 'M3LR',
      dealership_id: 'dealer_2',
      vin: '5YJ3E1EA4PF123789',
      disclaimer: 'Federal tax credit may apply. See dealer for eligibility.',
      eligible_regions: ['US', 'CA'],
      conditions: ['Must qualify for financing'],
      finance_terms: {
        apr: 3.9,
        down_payment: 5000,
        term_months: 60,
      },
    },
    {
      _id: '3',
      title: 'Mercedes C-Class',
      headline: 'German Engineering Excellence',
      content:
        'Experience the perfect blend of luxury and performance with the Mercedes C-Class. Premium interior, cutting-edge technology, and exceptional comfort.',
      name: 'Mercedes C-Class Deal',
      description: 'Luxury sedan with premium features',
      offer_type: 'LEASE',
      offer_image_url:
        'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/mercedes-c-class-offer',
      vehicle_make: 'MERCEDES',
      vehicle_model: 'C-CLASS',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&h=250&fit=crop',
      msrp: 55000,
      selling_price: 48400,
      drivetrain: 'RWD',
      trim: 'C 300',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-28',
      stock_number: 'MB003',
      model_code: 'W206',
      dealership_id: 'dealer_3',
      vin: 'WDDGF4HB5PR123456',
      disclaimer:
        'Available to qualified buyers. See dealer for complete terms.',
      eligible_regions: ['US'],
      conditions: ['Must lease through Mercedes Financial'],
      lease_terms: [
        {
          monthly_payment: 699,
          due_at_signing: 4000,
          lease_term_months: 36,
          annual_mileage_limit: 10000,
        },
        {
          monthly_payment: 599,
          due_at_signing: 5500,
          lease_term_months: 39,
          annual_mileage_limit: 12000,
        },
      ],
    },
    {
      _id: '4',
      title: 'Audi Q7 Premium',
      headline: 'Luxury SUV Redefined',
      content:
        'The Audi Q7 combines sophisticated design with powerful performance. Advanced Quattro all-wheel drive and spacious interior for the ultimate driving experience.',
      name: 'Audi Q7 Deal',
      description: 'Premium luxury SUV with Quattro AWD',
      offer_type: 'FINANCE',
      offer_image_url:
        'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/audi-q7-offer',
      vehicle_make: 'AUDI',
      vehicle_model: 'Q7',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400&h=250&fit=crop',
      msrp: 85000,
      selling_price: 69700,
      drivetrain: 'AWD',
      trim: 'Premium Plus',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-30',
      stock_number: 'AUD004',
      model_code: '4M',
      dealership_id: 'dealer_4',
      vin: 'WA1VAAF70PD123789',
      disclaimer: 'Finance rates subject to credit approval.',
      eligible_regions: ['ALL'],
      conditions: ['Must qualify for Audi Financial Services'],
      finance_terms: {
        apr: 4.2,
        down_payment: 8000,
        term_months: 72,
      },
    },
    {
      _id: '5',
      title: 'Porsche 911 Carrera',
      headline: 'Pure Sports Car Excellence',
      content:
        'The iconic Porsche 911 delivers legendary performance and timeless design. Experience the thrill of pure driving pleasure with this sports car icon.',
      name: 'Porsche 911 Deal',
      description: 'Iconic sports car with legendary performance',
      offer_type: 'CASH',
      offer_image_url:
        'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/porsche-911-offer',
      vehicle_make: 'PORSCHE',
      vehicle_model: '911',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&h=250&fit=crop',
      msrp: 120000,
      selling_price: 108000,
      drivetrain: 'RWD',
      trim: 'Carrera',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-20',
      stock_number: 'PCH005',
      model_code: '992',
      dealership_id: 'dealer_5',
      vin: 'WP0AA2A92PS123456',
      disclaimer: 'Cash purchase discount. No additional incentives apply.',
      eligible_regions: ['US', 'CA'],
      conditions: ['Cash purchase only'],
      cashback_amount: 5000,
    },
    {
      _id: '6',
      title: 'Range Rover Sport',
      headline: 'Luxury Meets Adventure',
      content:
        'The Range Rover Sport delivers uncompromising luxury with off-road capability. Premium materials, advanced technology, and commanding presence.',
      name: 'Range Rover Sport Deal',
      description: 'Luxury SUV with off-road capability',
      offer_type: 'LEASE',
      offer_image_url:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/range-rover-sport-offer',
      vehicle_make: 'LAND ROVER',
      vehicle_model: 'RANGE ROVER SPORT',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      msrp: 95000,
      selling_price: 71250,
      drivetrain: 'AWD',
      trim: 'SE',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      stock_number: 'LR006',
      model_code: 'L494',
      dealership_id: 'dealer_6',
      vin: 'SALWR2RV5PA123456',
      disclaimer: 'Lease terms subject to credit approval.',
      eligible_regions: ['US'],
      conditions: ['Must lease through Land Rover Financial'],
      lease_terms: [
        {
          monthly_payment: 1299,
          due_at_signing: 6000,
          lease_term_months: 36,
          annual_mileage_limit: 10000,
        },
      ],
    },
    {
      _id: '7',
      title: 'Lexus RX 350',
      headline: 'Luxury Redefined',
      content:
        'The Lexus RX 350 combines luxury, reliability, and performance. Featuring advanced safety technology and premium comfort for every journey.',
      name: 'Lexus RX Deal',
      description: 'Luxury SUV with hybrid efficiency',
      offer_type: 'FINANCE',
      offer_image_url:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/lexus-rx-offer',
      vehicle_make: 'LEXUS',
      vehicle_model: 'RX',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      msrp: 60000,
      selling_price: 54000,
      drivetrain: 'AWD',
      trim: '350',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-15',
      stock_number: 'LEX007',
      model_code: 'AL20',
      dealership_id: 'dealer_7',
      vin: 'JTJBZMCA5P2123456',
      disclaimer: 'Finance offers available to qualified buyers.',
      eligible_regions: ['ALL'],
      conditions: ['Must finance through Lexus Financial Services'],
      finance_terms: {
        apr: 3.5,
        down_payment: 6000,
        term_months: 60,
      },
    },
    {
      _id: '8',
      title: 'Cadillac Escalade',
      headline: 'American Luxury Icon',
      content:
        'The Cadillac Escalade represents the pinnacle of American luxury. Spacious interior, powerful performance, and cutting-edge technology.',
      name: 'Cadillac Escalade Deal',
      description: 'Full-size luxury SUV with premium features',
      offer_type: 'LEASE',
      offer_image_url:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/cadillac-escalade-offer',
      vehicle_make: 'CADILLAC',
      vehicle_model: 'ESCALADE',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      msrp: 110000,
      selling_price: 93500,
      drivetrain: 'AWD',
      trim: 'Premium Luxury',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-10',
      stock_number: 'CAD008',
      model_code: 'GMT1XX',
      dealership_id: 'dealer_8',
      vin: '1GYS4HKJ5PR123456',
      disclaimer: 'Lease offers available to qualified lessees.',
      eligible_regions: ['US', 'CA'],
      conditions: ['Must lease through GM Financial'],
      lease_terms: [
        {
          monthly_payment: 1599,
          due_at_signing: 8000,
          lease_term_months: 36,
          annual_mileage_limit: 12000,
        },
      ],
    },
    {
      _id: '9',
      title: 'Genesis GV80',
      headline: 'Korean Luxury Innovation',
      content:
        'The Genesis GV80 brings Korean luxury innovation to the premium SUV segment. Advanced technology, elegant design, and exceptional value.',
      name: 'Genesis GV80 Deal',
      description: 'Luxury SUV with advanced technology',
      offer_type: 'CASH',
      offer_image_url:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/genesis-gv80-offer',
      vehicle_make: 'GENESIS',
      vehicle_model: 'GV80',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      msrp: 70000,
      selling_price: 63000,
      drivetrain: 'AWD',
      trim: '3.5T',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-12-05',
      stock_number: 'GEN009',
      model_code: 'RG3',
      dealership_id: 'dealer_9',
      vin: 'KMUHB8SA5PA123456',
      disclaimer: 'Cash discount available for immediate purchase.',
      eligible_regions: ['ALL'],
      conditions: ['Cash payment required'],
      cashback_amount: 3000,
    },
    {
      _id: '10',
      title: 'Ford Mustang GT',
      headline: 'American Muscle Legend',
      content:
        'The Ford Mustang GT delivers pure American muscle car performance. Iconic design, powerful V8 engine, and thrilling driving experience.',
      name: 'Ford Mustang GT Deal',
      description: 'Classic American muscle car',
      offer_type: 'FINANCE',
      offer_image_url:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      offer_url: 'https://example.com/ford-mustang-gt-offer',
      vehicle_make: 'FORD',
      vehicle_model: 'MUSTANG',
      vehicle_year: 2024,
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      msrp: 55000,
      selling_price: 49500,
      drivetrain: 'RWD',
      trim: 'GT',
      is_active: true,
      start_date: '2024-01-01',
      end_date: '2024-11-30',
      stock_number: 'FRD010',
      model_code: 'S550',
      dealership_id: 'dealer_10',
      vin: '1FA6P8CF5P5123456',
      disclaimer: 'Finance rates based on approved credit.',
      eligible_regions: ['US'],
      conditions: ['Must finance through Ford Credit'],
      finance_terms: {
        apr: 4.1,
        down_payment: 5500,
        term_months: 60,
      },
    },
  ];

  // Infinite Query for offers - Fixed TypeScript issues
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useInfiniteQuery<ApiResponse, Error>({
    queryKey: [
      'offers',
      selectedDealerId, // Add dealership_id to query key
      debouncedSearchTerm,
      debouncedStatusFilter,
      debouncedCategoryFilter,
      debouncedDrivetrainFilter,
      debouncedIsActiveFilter,
      debouncedPriceRangeFilter,
      debouncedVehicleYearFilter,
      debouncedDiscountTypeFilter,
      debouncedSortBy,
    ],
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!selectedDealerId && isQueryEnabled, // Only fetch when dealer is selected and query is enabled
    queryFn: async ({ pageParam = 0 }): Promise<ApiResponse> => {
      try {
        // Convert filters to MUI DataGrid format using debounced values
        const filterModel = convertFiltersToDataGridFormat(
          debouncedSearchTerm,
          debouncedStatusFilter,
          debouncedCategoryFilter,
          debouncedDrivetrainFilter,
          debouncedIsActiveFilter,
          debouncedPriceRangeFilter,
          debouncedVehicleYearFilter,
          debouncedDiscountTypeFilter,
        );

        // Convert sort to MUI DataGrid format using debounced value
        const sortModel = convertSortToDataGridFormat(debouncedSortBy);

        const response = await Offers.getAllOffers({
          skip: pageParam as number,
          limit: ITEMS_PER_PAGE,
          filters: JSON.stringify(filterModel),
          sort: JSON.stringify(sortModel),
          dealer: selectedDealerId, // Always include dealership_id
        });

        // Check if API response is valid based on your image structure
        if (
          response &&
          response.body &&
          response.body.data &&
          Array.isArray(response.body.data) &&
          response.body.data.length > 0
        ) {
          return {
            data: response.body.data as OfferCard[],
            meta: response.body.meta || {
              totalDocuments: response.body.data.length,
              totalPages: 1,
              currentPage: 1,
            },
          };
        } else {
          // Return empty data when API response is invalid - no mock data
          return {
            data: [],
            meta: {
              totalDocuments: 0,
              totalPages: 0,
              currentPage: 1,
            },
          };
        }
      } catch (error) {
        // Return empty data on error - no mock data fallback
        console.error('Error fetching offers:', error);
        return {
          data: [],
          meta: {
            totalDocuments: 0,
            totalPages: 0,
            currentPage: 1,
          },
        };
      }
    },
    getNextPageParam: (lastPage, pages) => {
      const currentPage = lastPage.meta.currentPage;
      const totalPages = lastPage.meta.totalPages;

      if (currentPage < totalPages) {
        return pages.length * ITEMS_PER_PAGE; // This will give: 0, 9, 18, 27...
      }
      return undefined;
    },
    // Remove initialPageParam as it's causing TypeScript error
  });

  // Flatten all pages data into single array - Fixed TypeScript issues
  const offers = data?.pages.flatMap((page: ApiResponse) => page.data) || [];
  const totalOffers = data?.pages[0]?.meta?.totalDocuments || 0;
  const currentPage =
    data?.pages[data.pages.length - 1]?.meta?.currentPage || 1;

  // API Calls
  const addOffer = async (offer: Partial<OfferDetails> & { title: string }) => {
    setError('');
    try {
      await Offers.addOffer(offer);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add offer');
      throw err;
    }
  };

  const deleteOffer = async (offerId: string) => {
    setError('');
    try {
      await Offers.deleteOffer(offerId);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete offer');
      throw err;
    }
  };

  const editOffer = async (offerId: string, data: Partial<OfferDetails>) => {
    setError('');
    try {
      await Offers.editOffer(offerId, data);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to edit offer');
      throw err;
    }
  };

  const getOffer = async (offerId: string) => {
    setError('');
    try {
      const response = await Offers.getOffer(offerId);
      setSelectedOffer(response.body as OfferDetails);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch offer details',
      );
      throw err;
    }
  };

  const refetchOffers = useCallback(() => {
    refetch();
  }, [refetch]);

  // Reset offers when filters change - clear all cached pages
  const resetAndRefetchOffers = useCallback(() => {
    // Use invalidateQueries instead of removeQueries for better performance
    queryClient.invalidateQueries({
      queryKey: ['offers'],
      exact: false, // This will invalidate all queries starting with 'offers'
    });
  }, [queryClient]);

  // Enable query manually
  const enableQuery = useCallback(() => {
    setIsQueryEnabled(true);
  }, []);

  // Utility functions to convert UI filters to MUI DataGrid format - Schema based
  const convertFiltersToDataGridFormat = (
    searchTerm: string,
    statusFilter: string,
    category: string,
    drivetrain: string,
    isActive: boolean | null,
    priceRange: [number, number],
    vehicleYear: number | null,
    discountType: string,
  ) => {
    const filterItems: any[] = [];
    let id = 1;

    // Search term filter - schema field 'title'
    if (searchTerm) {
      filterItems.push({
        id: id++,
        columnField: 'title', // Backend expects 'title' field for search
        operatorValue: 'contains', // Backend expects 'operatorValue' instead of 'operator'
        value: searchTerm,
      });
    }

    // Offer type filter - schema field 'offer_type'
    if (statusFilter && statusFilter !== 'All Types') {
      filterItems.push({
        id: id++,
        columnField: 'offer_type',
        operatorValue: 'equals',
        value: statusFilter,
      });
    }

    // Vehicle make filter - schema field 'manufacturer'
    if (category && category !== 'All Makes') {
      filterItems.push({
        id: id++,
        columnField: 'manufacturer', // Schema uses manufacturer instead of vehicle_make
        operatorValue: 'equals',
        value: category,
      });
    }

    // Drivetrain filter - schema may have custom field name
    if (drivetrain && drivetrain !== 'All Drivetrain') {
      filterItems.push({
        id: id++,
        columnField: 'drivetrain', // Keep as drivetrain or check schema
        operatorValue: 'equals',
        value: drivetrain,
      });
    }

    // Active status filter - schema field 'is_active' (boolean) - only apply when toggle is ON
    if (isActive === true) {
      filterItems.push({
        id: id++,
        columnField: 'is_active', // Schema uses 'is_active' field for active/inactive
        operatorValue: 'isTrue', // Use 'isTrue' operator for boolean fields
        value: isActive.toString(),
      });
    }

    // Price range filter - schema field 'selling_price'
    if (priceRange[0] > 0 || priceRange[1] < 100000) {
      filterItems.push({
        id: id++,
        columnField: 'selling_price', // Schema uses 'selling_price'
        operatorValue: 'greaterThanOrEqual', // Backend expects 'greaterThanOrEqual' instead of '>='
        value: priceRange[0].toString(),
      });
      filterItems.push({
        id: id++,
        columnField: 'selling_price',
        operatorValue: 'lessThanOrEqual', // Backend expects 'lessThanOrEqual' instead of '<='
        value: priceRange[1].toString(),
      });
    }

    // Vehicle year filter - schema field 'car_year' or 'vehicle_year'
    if (vehicleYear) {
      filterItems.push({
        id: id++,
        columnField: 'car_year', // Schema uses car_year
        operatorValue: 'equals',
        value: vehicleYear.toString(),
      });
    }

    // Discount type filter - schema field 'discount_type'
    if (discountType && discountType !== 'All Discount Types') {
      filterItems.push({
        id: id++,
        columnField: 'discount_type',
        operatorValue: 'equals',
        value: discountType,
      });
    }

    return {
      items: filterItems,
      linkOperator: 'and', // Backend expects 'linkOperator' instead of 'logicOperator'
    };
  };

  const convertSortToDataGridFormat = (sortBy: string) => {
    if (!sortBy || sortBy === 'Newest First') {
      return [{ columnField: 'starts_at', sort: 'desc' }]; // Backend expects 'columnField' instead of 'field'
    }

    switch (sortBy) {
      case 'Oldest First':
        return [{ columnField: 'starts_at', sort: 'asc' }]; // Backend expects 'columnField' instead of 'field'
      case 'Price: Low to High':
        return [{ columnField: 'selling_price', sort: 'asc' }]; // Backend expects 'columnField' instead of 'field'
      case 'Price: High to Low':
        return [{ columnField: 'selling_price', sort: 'desc' }]; // Backend expects 'columnField' instead of 'field'
      case 'Title: A to Z':
        return [{ columnField: 'title', sort: 'asc' }]; // Backend expects 'title' field for sorting
      case 'Title: Z to A':
        return [{ columnField: 'title', sort: 'desc' }]; // Backend expects 'title' field for sorting
      case 'Year: Newest First':
        return [{ columnField: 'car_year', sort: 'desc' }]; // Backend expects 'columnField' instead of 'field'
      case 'Year: Oldest First':
        return [{ columnField: 'car_year', sort: 'asc' }]; // Backend expects 'columnField' instead of 'field'
      default:
        return [{ columnField: 'starts_at', sort: 'desc' }]; // Backend expects 'columnField' instead of 'field'
    }
  };

  return (
    <OffersContext.Provider
      value={{
        loading: isLoading,
        error: isError
          ? queryError instanceof Error
            ? queryError.message
            : 'Failed to fetch offers'
          : error,
        offers,
        selectedOffer,

        // Dealer selection
        selectedDealerId,
        selectedDealerName:
          selectedDealer?.site_title || selectedDealer?.name || '',

        // Infinite scroll properties
        hasNextPage: !!hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        totalOffers,
        currentPage,

        addOffer,
        deleteOffer,
        editOffer,
        getOffer,
        refetchOffers,
        resetAndRefetchOffers,
        enableQuery,

        // Filters
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        statusFilter,
        setStatusFilter,
        categoryFilter,
        setCategoryFilter,
        drivetrainFilter,
        setDrivetrainFilter,
        isActiveFilter,
        setIsActiveFilter,
        priceRangeFilter,
        setPriceRangeFilter,
        vehicleYearFilter,
        setVehicleYearFilter,
        discountTypeFilter,
        setDiscountTypeFilter,
      }}
    >
      {children}
    </OffersContext.Provider>
  );
};

export const useOffers = (): OffersContextType => {
  const context = useContext(OffersContext);
  if (context === undefined) {
    throw new Error('useOffers must be used within an OffersProvider');
  }
  return context;
};
